import { useState, useCallback, useEffect, useMemo } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useUserFiles } from '../../../../../hooks/query/user/useUserFiles'
import { UserFile } from '../../../../../service/user'

interface UseImageSelectionProps {
  isOpen: boolean
}

export const useImageSelection = ({ isOpen }: UseImageSelectionProps) => {
  const [activeTab, setActiveTab] = useState<'generated' | 'uploaded'>(
    'uploaded',
  )
  const [selectedFile, setSelectedFile] = useState<UserFile | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const queryClient = useQueryClient()

  // API 데이터 가져오기
  const generatedFiles = useUserFiles({
    fileType: 'ai_generated',
    limit: 20,
    enabled: isOpen,
  })

  const uploadedFiles = useUserFiles({
    fileType: 'direct_upload',
    limit: 20,
    enabled: isOpen,
  })

  // 현재 탭의 쿼리와 파일 데이터
  const currentQuery =
    activeTab === 'generated' ? generatedFiles : uploadedFiles

  const currentFiles = useMemo(() => {
    if (!currentQuery.data?.pages) return []

    const allFiles = currentQuery.data.pages
      .filter((page) => page && Array.isArray(page.data))
      .flatMap((page) => page.data)
      .filter((file) => file && (file.id || file.id === 0) && file.fileUrl)

    return allFiles
  }, [currentQuery.data, activeTab])

  // 상태 계산
  const isLoading = currentQuery.isLoading && isOpen
  const error = currentQuery.error
  const isFetchingMore = currentQuery.isFetchingNextPage
  const hasData = currentFiles.length > 0

  // 탭 변경 시 쿼리 강제 refetch
  useEffect(() => {
    if (isOpen) {
      if (activeTab === 'generated') {
        generatedFiles.refetch()
      } else if (activeTab === 'uploaded') {
        uploadedFiles.refetch()
      }
    }
  }, [activeTab, isOpen])

  // 모달이 열릴 때 캐시 무효화 및 상태 초기화
  useEffect(() => {
    if (isOpen) {
      queryClient.invalidateQueries({ queryKey: ['user', 'files'] })
      setSelectedFile(null)
    }
  }, [isOpen, queryClient])

  // 파일 목록이 로드되면 첫 번째 파일을 자동 선택
  useEffect(() => {
    if (isOpen && currentFiles.length > 0) {
      setSelectedFile(currentFiles[0])
    }
  }, [isOpen, currentFiles])

  // 이벤트 핸들러
  const handleTabChange = useCallback((tab: 'generated' | 'uploaded') => {
    setActiveTab(tab)
  }, [])

  const handleImageClick = useCallback((file: UserFile) => {
    setSelectedFile(file)
  }, [])

  const handleLoadMore = useCallback(() => {
    if (currentQuery.hasNextPage) {
      currentQuery.fetchNextPage()
    }
  }, [currentQuery])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const refreshCurrentFiles = useCallback(() => {
    currentQuery.refetch()
  }, [currentQuery])

  return {
    // State
    activeTab,
    selectedFile,
    isDragOver,
    currentFiles,
    isLoading,
    error,
    isFetchingMore,
    hasData,

    // Queries
    generatedFiles,
    uploadedFiles,
    currentQuery,

    // Handlers
    handleTabChange,
    handleImageClick,
    handleLoadMore,
    handleDragOver,
    handleDragLeave,
    refreshCurrentFiles,
    setIsDragOver,
  }
}
